import { configureStore, createListenerMiddleware } from "@reduxjs/toolkit";
import patientTableRecordsReducer from "../state/redux/patientTableRecordsSlice";
import doctorSlice from "@redux/doctorSlice";
import resourcesSlice, { resourceSelected, resourcesSelected, clearResourceState } from "@redux/resourcesSlice";
import documentSlice from "@redux/documentSlice";
import activeDocumentsSlice from "@features/patient-view/state/redux/activeDocumentsSlice";
import activePatientDataSlice from "@features/patient-view/state/redux/activePatientDataSlice";
import globalStateSlice from "@redux/globalState";
import mergeVisitsUiSlice from "@redux/mergeVisitsUiSlice";
import reviewSessionReducer from "@features/review-mode/state/reviewSessionSlice";
import { reviewSessionPersistenceMiddleware } from "@features/review-mode/state/reviewSessionPersistence";

// Persist resource selection to localStorage as a side effect of actions,
// keeping reducers pure. Runs after the reducer has updated state.
const resourcesPersistenceMiddleware = createListenerMiddleware();

resourcesPersistenceMiddleware.startListening({
    actionCreator: resourceSelected,
    effect: (action) => {
        localStorage.setItem(
            "selectedResource",
            JSON.stringify({ accountId: action.payload.accountId, data: action.payload.resource }),
        );
    },
});

resourcesPersistenceMiddleware.startListening({
    actionCreator: resourcesSelected,
    effect: (action) => {
        const { resources, accountId } = action.payload;
        localStorage.setItem("selectedResources", JSON.stringify({ accountId, data: resources }));
        if (resources.length > 0) {
            localStorage.setItem("selectedResource", JSON.stringify({ accountId, data: resources[0] }));
        } else {
            localStorage.removeItem("selectedResource");
        }
    },
});

resourcesPersistenceMiddleware.startListening({
    actionCreator: clearResourceState,
    effect: () => {
        localStorage.removeItem("selectedResource");
        localStorage.removeItem("selectedResources");
    },
});

const store = configureStore({
    reducer: {
        patientTableRecords: patientTableRecordsReducer,
        doctors: doctorSlice,
        resources: resourcesSlice,
        documents: documentSlice,
        activeDocuments: activeDocumentsSlice,
        activePatientData: activePatientDataSlice,
        globalState: globalStateSlice,
        mergeVisitsUi: mergeVisitsUiSlice,
        reviewSession: reviewSessionReducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().prepend(
            resourcesPersistenceMiddleware.middleware,
            reviewSessionPersistenceMiddleware.middleware,
        ),
});

// infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
